import { useState, useEffect } from "react";
import { useGlobals } from "../../hooks/useGlobals";
import { MemberAuthType } from "../../lib/enums/member.enum";
import { useSearchParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Profile from "./Profile";
import MyInterviews from "./MyInterviews";

type TabType = "profile" | "interviews";

function MyPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("profile");
  const { authMember } = useGlobals();

  const tabs = [
    { id: "profile" as TabType, name: t("myPage.tabs.profile"), icon: "👤" },
    {
      id: "interviews" as TabType,
      name: t("myPage.tabs.interviews"),
      icon: "📝",
    },
  ];

  useEffect(() => {
    if (!authMember) {
      navigate("/login");
      return;
    }
  }, [authMember, navigate]);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "profile" || tabParam === "interviews") {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`/my-page?tab=${tab}`);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: "4rem" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t("myPage.title")}
          </h1>
          <p className="text-gray-600">{t("myPage.subtitle")}</p>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar - Tabs */}
          <div className="w-full md:w-64 bg-white rounded-xl shadow-lg p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? "bg-indigo-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span className="text-2xl">{tab.icon}</span>
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 bg-white rounded-xl shadow-lg p-6 md:p-8">
            {activeTab === "profile" && <Profile />}
            {activeTab === "interviews" && <MyInterviews />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyPage;
